import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";

import { Auth } from "@aws-amplify/auth";
import { API, GraphQLResult, graphqlOperation } from "@aws-amplify/api";
import { GetSkillInstanceQuery, SkillInstance } from "../../src/API";
import * as queries from "../../src/graphql/queries";
import * as subscriptions from "../../src/graphql/subscriptions";

import { useAuth0 } from "@auth0/auth0-react";

import Layout from "../../components/layouts/dashboard";
import SkillInstanceView from "../../components/dashboard/skills/skill-instance-view";
import { Observable, ZenObservable } from "zen-observable-ts";

export default function Page() {
  const [instance, setInstance] = useState<Partial<SkillInstance>>();
  const subInstRef = useRef<ZenObservable.Subscription>();
  const subTempRef = useRef<ZenObservable.Subscription>();

  const router = useRouter();
  const { getAccessTokenSilently } = useAuth0();

  const { instanceId } = router.query;

  const getInstance = useCallback(async () => {
    if (!instanceId) {
      return;
    }

    const token = await getAccessTokenSilently();
    const gqlGetSkill = (await API.graphql(
      graphqlOperation(queries.getSkillInstance, { id: instanceId }, token)
    )) as GraphQLResult<GetSkillInstanceQuery>;
    const result = gqlGetSkill?.data?.getSkillInstance;
    setInstance(result as SkillInstance);
  }, [getAccessTokenSilently, instanceId]);

  useEffect(() => {
    getInstance();
  }, [getInstance]);

  const updateSub = useCallback(async () => {
    if (!instance?.id) return;

    const token = await getAccessTokenSilently();
    // if the idtoken is expired, this will trigger a refresh on page (re)load
    try {
      await Auth.currentCredentials();
    } catch {}

    console.info("subscribing to instance");
    subInstRef.current = (
      API.graphql(
        graphqlOperation(
          subscriptions.onSkillInstanceById,
          { id: instance.id },
          token
        )
      ) as Observable<object>
    ).subscribe({
      next: ({ value }: { value: any }) => {
        getInstance();
      },
      error: (error: any) => {
        console.warn(`instance subscription error ${error}. retrying.`);
        updateSub();
      },
    });

    if (!instance?.template?.id) return;
    console.info("subscribing to template");
    subTempRef.current = (
      API.graphql(
        graphqlOperation(
          subscriptions.onSkillTemplateById,
          { id: instance.template.id },
          token
        )
      ) as Observable<object>
    ).subscribe({
      next: ({ value }: { value: any }) => {
        getInstance();
      },
      error: (error: any) => {
        console.warn(`template subscription error ${error}. retrying.`);
        updateSub();
      },
    });
  }, [
    instance?.id,
    instance?.template?.id,
    getAccessTokenSilently,
    getInstance,
  ]);

  useEffect(() => {
    updateSub();

    return () => {
      console.info("unsubscribing from instance");
      subInstRef?.current?.unsubscribe();
      console.info("unsubscribing from template");
      subTempRef?.current?.unsubscribe();
    };
  }, [updateSub]);

  return (
    <div className="h-full overflow-y-auto p-4">
      <div className="mb-4 text-center">
        <h1 className="text-xl">Skill: {instance?.template?.name}</h1>
      </div>
      {instance ? (
        <SkillInstanceView
          skill={instance}
          refresh={getInstance}
        ></SkillInstanceView>
      ) : null}
    </div>
  );
}

Page.getLayout = function getLayout(page: any) {
  return <Layout title="Skill Details">{page}</Layout>;
};
